import { createContext, useContext, useState, useRef } from "react";
import { GrAttachment } from "react-icons/gr";
import { MdDeleteOutline } from "react-icons/md";
import { toast } from "react-toastify";

const backend_url = process.env.REACT_APP_BACKEND_SERVICE_URL;
// const backend_url = "https://datagenx-backend-srv-801923314065.europe-west3.run.app";

const FileUploadContext = createContext();

export const FileUploadProvider = ({ children }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [isSent, setIsSent] = useState(false);
  const [uploading, setUploading] = useState(false);

  const removeFile = () => {
    if (uploading) return;
    setSelectedFile(null);
    setIsSent(false);
  };

  const uploadSelectedFile = async (platform) => {
    const userId = localStorage.getItem("userId");
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("user_id", userId);
    formData.append("platform_id", platform);

    try {
      setUploading(true);
      const res = await fetch(`${backend_url}/upload_file`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (res.ok && data.sucess) {
        setIsSent(true);
        return data;
      } else {
        throw new Error(data.message || "Failed");
      }
    } catch (err) {
      toast.error("Upload failed!");
      setIsSent(false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <FileUploadContext.Provider
      value={{
        selectedFile,
        setSelectedFile,
        isSent,
        setIsSent,
        uploading,
        setUploading,
        removeFile,
        uploadSelectedFile,
      }}
    >
      {children}
    </FileUploadContext.Provider>
  );
};

export const useFileUpload = () => useContext(FileUploadContext);

export const UploadButton = () => {
  const fileInputRef = useRef(null);
  const { setSelectedFile, setIsSent, uploading } = useFileUpload();

  const MAX_FILE_SIZE = 25 * 1024 * 1024; // 25 MB

  const ALLOWED_TYPES = [
    "text/x-c",
    "text/x-c++",
    "text/x-csharp",
    "text/css",
    "text/csv",
    "application/vnd.ms-excel",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.template",
    "text/x-golang",
    "text/html",
    "text/x-java",
    "text/javascript",
    "application/json",
    "text/markdown",
    "application/pdf",
    "text/x-php",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    "text/x-python",
    "text/x-script.python",
    "text/x-ruby",
    "application/x-sh",
    "text/x-tex",
    "application/typescript",
    "text/plain",
    "text/xml",
    "application/xml",
    "text/yaml",
    "application/x-yaml",
  ];

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE) {
      toast.error("File too large! Max 25MB allowed.");
      e.target.value = "";
      return;
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error("File type not supported!");
      e.target.value = "";
      return;
    }

    setSelectedFile(file);
    setIsSent(false);
    e.target.value = "";
  };

  return (
    <div
      className="absolute left-8 z-40"
      style={{ top: "37%", color: "rgb(150, 150, 150)" }}
    >
      <button
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
      >
        <GrAttachment size={18} />
      </button>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        accept=".c,.cpp,.cs,.css,.csv,.doc,.docx,.dotx,.go,.html,.java,.js,.json,.md,.pdf,.php,.pptx,.py,.rb,.sh,.tex,.ts,.txt,.xml,.yaml,.yml"
        onChange={handleFileChange}
        disabled={uploading}
      />
    </div>
  );
};

export const SelectedFilePreview = () => {
  const { selectedFile, removeFile, uploading } = useFileUpload();
  if (!selectedFile) return null;
  return (
    <div
      style={{
        border: "1px dashed #007bff",
        padding: "8px",
        display: "flex",
        gap: "6px",
        justifyContent: "space-between",
        right: "75px",
      }}
      className="absolute z-5000 bottom-15"
    >
      <span>
        {selectedFile.name.length > 16
          ? `${selectedFile.name.slice(0, 11)} ... ${selectedFile.name.slice(-5)}`
          : selectedFile.name}
      </span>
      <button
        onClick={removeFile}
        style={{ cursor: uploading ? "not-allowed" : "pointer" }}
      >
        <MdDeleteOutline size={20} color="red" />
      </button>
    </div>
  );
};
